import React, { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import "../App.css";
import PageMeta from './PageMeta';
import { OrganizationSchema, LocalBusinessSchema } from './SchemaMarkup';

const AboutUs = () => {
  const isMobile = typeof window !== 'undefined' && window.innerWidth <= 768;
  const location = useLocation();

  useEffect(() => {
    const hash = window.location.hash.substring(1);
    if (hash) {
      const element = document.getElementById(hash);
      if (element) {
        setTimeout(() => element.scrollIntoView({ behavior: 'smooth' }), 100);
      }
    } else {
      window.scrollTo(0, 0);
    }
  }, [location]);

  return (
    <div className="landing-page">
      <PageMeta 
        title="About Tenant Dispatch - Property Maintenance Made Simple"
        description="Learn how Tenant Dispatch is revolutionizing property management with AI-powered maintenance scheduling and professional service coordination."
        keywords="about tenant dispatch, property management company, maintenance software, service professionals"
      />
      
      <OrganizationSchema />
      <LocalBusinessSchema />
      
      {/* Navigation */}
      <nav className="landing-nav">
        <Link to="/" style={{ textDecoration: 'none' }}>
          <div className="nav-brand">
            <span className="brand-icon">🏠</span>
            <span className="brand-text">Tenant Dispatch</span>
          </div>
        </Link>
        <div className="nav-links">
          <Link to="/" style={{ color: '#64748b', textDecoration: 'none', fontWeight: 500 }}>Home</Link>
          <a href="#mission">Mission</a>
          <a href="#team">Team</a>
          <a href="#contact">Contact</a>
        </div>
      </nav>

      {/* Hero Section */}
      <section style={{
        background: 'linear-gradient(135deg, #1e293b 0%, #334155 50%, #1e293b 100%)',
        padding: '80px 80px 60px',
        textAlign: 'center',
        position: 'relative',
        overflow: 'hidden'
      }}>
        <div style={{ position: 'relative', zIndex: 1, maxWidth: 800, margin: '0 auto' }}>
          <div className="hero-badge" style={{ background: 'rgba(59,130,246,0.15)', color: '#93c5fd' }}>
            About Us
          </div>
          <h1 style={{
            fontSize: '3.2rem',
            fontWeight: 800,
            color: '#fff',
            lineHeight: 1.15,
            margin: '0 0 24px'
          }}>
            We're Building the Future of{' '}
            <span className="gradient-text">Property Management</span>
          </h1>
          <p style={{
            fontSize: '1.25rem',
            color: '#94a3b8',
            lineHeight: 1.7,
            maxWidth: 600,
            margin: '0 auto'
          }}>
            Tenant Dispatch was founded with a simple belief: property management should be effortless, transparent, and automated.
          </p>
        </div>
      </section>

      {/* Stats Bar */}
      <section style={{
        display: 'flex',
        justifyContent: 'center',
        gap: '60px',
        padding: '40px 40px',
        background: '#fff',
        borderBottom: '1px solid #e2e8f0',
        flexWrap: 'wrap'
      }}>
        {[
          { number: '500+', label: 'Property Owners' },
          { number: '10,000+', label: 'Units Managed' },
          { number: '50,000+', label: 'Requests Resolved' },
          { number: '99.9%', label: 'Uptime' }
        ].map((stat, i) => (
          <div key={i} style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '2.5rem', fontWeight: 800, color: '#1e293b' }}>{stat.number}</div>
            <div style={{ fontSize: '0.95rem', color: '#64748b', fontWeight: 500, marginTop: 4 }}>{stat.label}</div>
          </div>
        ))}
      </section>

      {/* Mission Section */}
      <section id="mission" style={{ padding: '80px 80px 60px', maxWidth: 1200, margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: 40 }}>
          <span className="section-badge">Our Mission</span>
          <h2 className="section-title" style={{ marginTop: 16 }}>
            Empowering Property Owners to Do More with Less
          </h2>
        </div>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
          gap: 30
        }}>
          <div style={{
            background: '#f8fafc',
            borderRadius: 16,
            padding: '40px 30px',
            border: '1px solid #e2e8f0'
          }}>
            <div style={{ fontSize: '2.5rem', marginBottom: 16 }}>🎯</div>
            <h3 style={{ fontSize: '1.3rem', fontWeight: 700, color: '#1e293b', marginBottom: 12 }}>Our Vision</h3>
            <p style={{ color: '#64748b', lineHeight: 1.7 }}>
              We envision a world where property owners never have to play middleman between tenants and service professionals. Our platform handles everything — from request intake to resolution — so you can focus on growing your portfolio.
            </p>
          </div>
          <div style={{
            background: '#f8fafc',
            borderRadius: 16,
            padding: '40px 30px',
            border: '1px solid #e2e8f0'
          }}>
            <div style={{ fontSize: '2.5rem', marginBottom: 16 }}>💡</div>
            <h3 style={{ fontSize: '1.3rem', fontWeight: 700, color: '#1e293b', marginBottom: 12 }}>The Problem We Solve</h3>
            <p style={{ color: '#64748b', lineHeight: 1.7 }}>
              Managing maintenance requests manually wastes hours every week. Phone calls, texts, emails — it's chaos. Tenant Dispatch replaces that with a single, automated workflow that keeps everyone informed in real time.
            </p>
          </div>
          <div style={{
            background: '#f8fafc',
            borderRadius: 16,
            padding: '40px 30px',
            border: '1px solid #e2e8f0'
          }}>
            <div style={{ fontSize: '2.5rem', marginBottom: 16 }}>🚀</div>
            <h3 style={{ fontSize: '1.3rem', fontWeight: 700, color: '#1e293b', marginBottom: 12 }}>What Drives Us</h3>
            <p style={{ color: '#64748b', lineHeight: 1.7 }}>
              We're property owners ourselves. We built the tool we wished existed — one that's simple enough for a first-time landlord, yet powerful enough for large-scale property managers.
            </p>
          </div>
        </div>
      </section>

      {/* Our Story */}
      <section style={{
        padding: '80px 80px 60px',
        background: 'linear-gradient(180deg, #f1f5f9 0%, #fff 100%)'
      }}>
        <div style={{ maxWidth: 800, margin: '0 auto', textAlign: 'center' }}>
          <span className="section-badge">Our Story</span>
          <h2 className="section-title" style={{ marginTop: 16 }}>
            From Frustration to Innovation
          </h2>
          <p style={{ color: '#64748b', lineHeight: 1.8, fontSize: '1.1rem', marginTop: 24 }}>
            Tenant Dispatch started in 2022 when our founders — a team of property managers and software engineers — grew tired of juggling phone calls, spreadsheets, and sticky notes to manage maintenance requests across their rental units.
          </p>
          <p style={{ color: '#64748b', lineHeight: 1.8, fontSize: '1.1rem', marginTop: 16 }}>
            They knew there had to be a better way. So they built one. What began as an internal tool quickly became a full-featured platform trusted by hundreds of property owners across the country. Today, Tenant Dispatch processes thousands of maintenance requests every month, saving owners an average of 10 hours per week.
          </p>
          <p style={{ color: '#64748b', lineHeight: 1.8, fontSize: '1.1rem', marginTop: 16 }}>
            We're just getting started. Our roadmap includes AI-powered diagnostics, vendor marketplace integration, and predictive maintenance — all designed to make property ownership simpler than ever.
          </p>
        </div>
      </section>

      {/* Values */}
      <section style={{ padding: '80px 80px 60px', maxWidth: 1200, margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: 40 }}>
          <span className="section-badge">Our Values</span>
          <h2 className="section-title" style={{ marginTop: 16 }}>
            What We Stand For
          </h2>
        </div>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
          gap: 30
        }}>
          {[
            { icon: '🔍', title: 'Transparency', desc: 'Every stakeholder sees real-time status. No black boxes, no surprises.' },
            { icon: '⚡', title: 'Speed', desc: 'We obsess over reducing time-to-resolution for every maintenance request.' },
            { icon: '🤝', title: 'Customer First', desc: 'Our product decisions start with what property owners and tenants actually need.' },
            { icon: '🔒', title: 'Reliability', desc: '99.9% uptime. Your tenants can submit requests and get updates 24/7.' }
          ].map((v, i) => (
            <div key={i} style={{
              textAlign: 'center',
              padding: '30px 20px',
              borderRadius: 12,
              border: '1px solid #e2e8f0',
              transition: 'transform 0.2s, box-shadow 0.2s',
              cursor: 'default'
            }}>
              <div style={{ fontSize: '2.2rem', marginBottom: 12 }}>{v.icon}</div>
              <h3 style={{ fontSize: '1.1rem', fontWeight: 700, color: '#1e293b', marginBottom: 8 }}>{v.title}</h3>
              <p style={{ color: '#64748b', fontSize: '0.95rem', lineHeight: 1.6 }}>{v.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Founder */}
      <section id="team" style={{
        padding: '80px 80px 60px',
        background: 'linear-gradient(180deg, #f1f5f9 0%, #fff 100%)'
      }}>
        <div style={{ textAlign: 'center', marginBottom: 40 }}>
          <span className="section-badge">Founder</span>
          <h2 className="section-title" style={{ marginTop: 16 }}>
            Meet Matti Bornstein
          </h2>
          <p style={{ color: '#64748b', maxWidth: 600, margin: '16px auto 0', lineHeight: 1.7 }}>
            The founder and visionary behind Tenant Dispatch, building the future of property management.
          </p>
        </div>
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          maxWidth: 400,
          margin: '0 auto'
        }}>
          <div style={{
            background: '#fff',
            borderRadius: 16,
            padding: '40px 30px',
            textAlign: 'center',
            border: '1px solid #e2e8f0',
            boxShadow: '0 4px 20px rgba(0,0,0,0.04)'
          }}>
            <img 
              src="/matti-bornstein.jpg"
              alt="Matti Bornstein"
              style={{
                width: 140,
                height: 140,
                borderRadius: '50%',
                objectFit: 'cover',
                margin: '0 auto 20px',
                border: '3px solid #3b82f6'
              }}
            />
            <h3 style={{ fontSize: '1.4rem', fontWeight: 700, color: '#1e293b', margin: '0 0 4px' }}>Matti Bornstein</h3>
            <p style={{ color: '#3b82f6', fontWeight: 600, fontSize: '1rem', margin: '0 0 16px' }}>CEO & Founder</p>
            <p style={{ color: '#64748b', fontSize: '0.95rem', lineHeight: 1.7, margin: 0 }}>15+ years in property management and real estate ownership. Technology consultant and app developer with proven expertise solving complex technical challenges. Founded Tenant Dispatch to revolutionize how property owners and professionals connect.</p>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" style={{
        padding: '80px 80px 60px',
        background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
        textAlign: 'center'
      }}>
        <div style={{ maxWidth: 600, margin: '0 auto' }}>
          <h2 style={{ fontSize: '2.5rem', fontWeight: 800, color: '#fff', margin: '0 0 16px' }}>
            Get in Touch
          </h2>
          <p style={{ color: '#94a3b8', fontSize: '1.1rem', lineHeight: 1.7, marginBottom: 40 }}>
            Have questions about Tenant Dispatch? Want a personalized demo? We'd love to hear from you. Our team responds to all inquiries within 1 business day.
          </p>

          {isMobile ? (
            <a
              href="mailto:contact@tenantdispatch.com"
              style={{
                display: 'inline-block',
                background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                color: '#fff',
                padding: '16px 40px',
                borderRadius: 10,
                fontSize: '1.1rem',
                fontWeight: 600,
                textDecoration: 'none',
                transition: 'transform 0.2s, box-shadow 0.2s'
              }}
            >
              📧 Email Us
            </a>
          ) : (
            <div style={{
              background: 'rgba(255,255,255,0.08)',
              borderRadius: 16,
              padding: '30px 40px',
              border: '1px solid rgba(255,255,255,0.1)',
              display: 'inline-block'
            }}>
              <p style={{ color: '#94a3b8', fontSize: '0.9rem', marginBottom: 8 }}>Email us at</p>
              <a
                href="mailto:contact@tenantdispatch.com"
                style={{
                  color: '#fff',
                  fontSize: '1.5rem',
                  fontWeight: 700,
                  display: 'block',
                  margin: '0 0 16px',
                  textDecoration: 'none',
                  transition: 'color 0.2s'
                }}
                onMouseEnter={(e) => e.target.style.color = '#93c5fd'}
                onMouseLeave={(e) => e.target.style.color = '#fff'}
              >
                contact@tenantdispatch.com
              </a>
              <a
                href="mailto:contact@tenantdispatch.com"
                style={{
                  display: 'inline-block',
                  background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                  color: '#fff',
                  border: 'none',
                  padding: '12px 32px',
                  borderRadius: 8,
                  fontSize: '1rem',
                  fontWeight: 600,
                  textDecoration: 'none',
                  transition: 'transform 0.2s, box-shadow 0.2s'
                }}
              >
                📧 Send Us an Email
              </a>
            </div>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="landing-footer">
        <div className="footer-content">
          <div className="footer-brand">
            <span className="brand-icon">🏠</span>
            <span className="brand-text">Tenant Dispatch</span>
            <p className="footer-tagline">Automated property maintenance for modern landlords.</p>
          </div>
          <div className="footer-links">
            <div className="footer-column">
              <h4>Product</h4>
              <Link to="/">Home</Link>
              <a href="#mission">Mission</a>
              <a href="#team">Team</a>
            </div>
            <div className="footer-column">
              <h4>Company</h4>
              <Link to="/about">About</Link>
              <Link to="/contact">Contact</Link>
            </div>
            <div className="footer-column">
              <h4>Support</h4>
              <Link to="/contact">Contact Us</Link>
            </div>
          </div>
        </div>
        <div className="footer-bottom">
          <p>&copy; {new Date().getFullYear()} Tenant Dispatch. All rights reserved.</p>
          <div className="footer-legal">
            <Link to="/privacy">Privacy Policy</Link>
            <Link to="/subprocessors">Subprocessors</Link>
            <Link to="/terms">Terms of Service</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default AboutUs;
